import type { DynamicPortfolio } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useClientPreferences,
  useCollectionRiskScores,
  useCreateDynamicPortfolio,
  usePlatforms,
  useUpdateDynamicPortfolio,
} from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";

import { PlatformPicker } from "../../platform-picker";
import { RiskScoreField } from "../portfolio-weights";
import { T } from "./dynamic-portfolio-dialog.i18n";
import { PreferenceMaximumFields } from "./preference-maximum-fields";
import { readPreferenceMaximums } from "./preference-maximums";

type DynamicPortfolioDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portfolioCollectionId: string;
  /** The dynamic portfolio being edited; null creates one. */
  portfolio: Pick<
    DynamicPortfolio,
    "id" | "riskScore" | "platformIds" | "maxPerPreference" | "maxTotalPreferences"
  > | null;
};

/**
 * Risk score, platforms and preference maximums of a dynamic portfolio — the
 * counterpart of the model PortfolioDialog. Its products are edited on the
 * portfolio's own page. The maximums are offered only when the collection has
 * client preferences.
 */
export function DynamicPortfolioDialog({
  open,
  onOpenChange,
  portfolioCollectionId,
  portfolio,
}: DynamicPortfolioDialogProps) {
  const t = useT(T);

  const createDynamicPortfolio = useCreateDynamicPortfolio();
  const updateDynamicPortfolio = useUpdateDynamicPortfolio();
  const { riskScores } = useCollectionRiskScores(portfolioCollectionId);
  const { platforms } = usePlatforms(portfolioCollectionId);
  const { clientPreferences } = useClientPreferences(portfolioCollectionId);
  const showPreferenceTilt = clientPreferences.length > 0;

  const isEditing = !!portfolio;
  const isSubmitting = createDynamicPortfolio.isPending || updateDynamicPortfolio.isPending;
  const [riskScore, setRiskScore] = useState("");
  const [platformIds, setPlatformIds] = useState<string[]>([]);
  const [perPreference, setPerPreference] = useState("0");
  const [total, setTotal] = useState("0");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setRiskScore(portfolio ? portfolio.riskScore.toString() : "");
    setPlatformIds(portfolio ? portfolio.platformIds : []);
    setPerPreference(String(portfolio?.maxPerPreference ?? 0));
    setTotal(String(portfolio?.maxTotalPreferences ?? 0));
  }, [open, portfolio]);

  const maximums = readPreferenceMaximums(perPreference, total);
  const hasInvalidMaximums = showPreferenceTilt && maximums.error !== null;

  const handleSubmit = () => {
    setError(null);
    if (hasInvalidMaximums) return;
    // Hidden fields send nothing: a new portfolio gets 0, an existing one keeps its values.
    const tilt =
      showPreferenceTilt && maximums.error === null
        ? {
            maxPerPreference: maximums.maxPerPreference,
            maxTotalPreferences: maximums.maxTotalPreferences,
          }
        : {};

    const callbacks = {
      onSuccess: () => onOpenChange(false),
      onError: (err: Error) => {
        setError(err.message || (isEditing ? t.failedToUpdate : t.failedToCreate));
      },
    };

    if (portfolio) {
      updateDynamicPortfolio.mutate(
        {
          collectionId: portfolioCollectionId,
          dynamicPortfolioId: portfolio.id,
          data: { platformIds, ...tilt },
        },
        callbacks,
      );
      return;
    }

    if (!riskScore) {
      setError(t.riskScoreRequired);
      return;
    }
    createDynamicPortfolio.mutate(
      {
        collectionId: portfolioCollectionId,
        data: { riskScore: parseInt(riskScore, 10), platformIds, ...tilt },
      },
      callbacks,
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {portfolio ? t.editTitle(String(portfolio.riskScore)) : t.createTitle}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t.editDescription : t.createDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
          )}

          <RiskScoreField
            value={riskScore}
            onChange={setRiskScore}
            scores={riskScores}
            readOnly={isEditing}
            hint={t.riskScoreHint}
          />

          <PlatformPicker
            platforms={platforms}
            value={platformIds}
            onChange={setPlatformIds}
            label={t.platforms}
            hint={t.platformsHint}
            idPrefix="dynamic-portfolio-platform"
            disabled={isSubmitting}
          />

          {showPreferenceTilt && (
            <div className="space-y-2">
              <div className="space-y-1">
                <p className="text-sm font-medium">{t.preferenceTilt}</p>
                <p className="text-sm text-muted-foreground">{t.preferenceTiltHint}</p>
              </div>
              <PreferenceMaximumFields
                idPrefix="dynamic-portfolio-dialog"
                perPreference={perPreference}
                total={total}
                onPerPreferenceChange={setPerPreference}
                onTotalChange={setTotal}
                disabled={isSubmitting}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogCancel disabled={isSubmitting}>{t.cancel}</DialogCancel>
          <Button
            onClick={handleSubmit}
            disabled={(!isEditing && riskScores.length === 0) || hasInvalidMaximums}
            loading={isSubmitting}
          >
            {isEditing ? t.save : t.addDynamicPortfolio}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
